/**
 * 학부모 대시보드 페이지
 * - 자녀별 이용권 현황
 * - 알림장 미읽음 수
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';
import { API_BASE_URL } from '../../../config/api';

interface TicketInfo {
  type: string;
  status: string;
  expires_at: string | null;
  remaining_hours: number | null;
  days_left: number | null;
}

interface ChildData {
  id: string;
  name: string;
  ticket: TicketInfo;
}

interface DashboardData {
  children: ChildData[];
  unread_notice_count: number;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { parent } = useSupabaseParentAuth();

  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (parent?.id) {
      loadDashboard();
    }
  }, [parent?.id]);

  const loadDashboard = async () => {
    setIsLoading(true);
    setError('');

    try {
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/parent/dashboard`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '대시보드 로드 실패');
      }

      const result = await response.json();
      if (result.success) {
        setDashboardData(result.data);
      }
    } catch (err: any) {
      console.error('대시보드 로드 오류:', err);
      setError(err.message || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatExpiryDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const getTicketStatusColor = (ticket: TicketInfo): 'success' | 'warning' | 'error' | 'default' => {
    if (ticket.status === 'none') return 'default';
    if (ticket.days_left !== null && ticket.days_left <= 3) return 'warning';
    if (ticket.days_left !== null && ticket.days_left <= 0) return 'error';
    return 'success';
  };

  const getTicketDescription = (ticket: TicketInfo): string => {
    if (ticket.status === 'none') return '이용권 없음';

    const parts: string[] = [ticket.type];

    if (ticket.expires_at) {
      parts.push(`${formatExpiryDate(ticket.expires_at)} 만료`);
      if (ticket.days_left !== null) {
        if (ticket.days_left > 0) {
          parts.push(`D-${ticket.days_left}`);
        } else if (ticket.days_left === 0) {
          parts.push('오늘 만료');
        } else {
          parts.push('만료됨');
        }
      }
    }

    if (ticket.remaining_hours !== null) {
      parts.push(`잔여 ${ticket.remaining_hours}시간`);
    }

    return parts.join(' | ');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* 인사말 */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          안녕하세요, {parent?.name || '학부모'}님
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 이용권 현황 */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ConfirmationNumberIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              이용권 현황
            </Typography>
          </Box>

          {!dashboardData?.children || dashboardData.children.length === 0 ? (
            <Typography color="text.secondary">등록된 자녀가 없습니다.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dashboardData.children.map((child) => {
                const statusColor = getTicketStatusColor(child.ticket);
                const isExpiringSoon = child.ticket.days_left !== null && child.ticket.days_left <= 3 && child.ticket.days_left >= 0;

                return (
                  <Box
                    key={child.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: statusColor === 'warning' ? 'warning.50' : statusColor === 'error' ? 'error.50' : 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: statusColor === 'warning' ? 'warning.200' : statusColor === 'error' ? 'error.200' : 'grey.200',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {child.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {isExpiringSoon && <WarningAmberIcon fontSize="small" color="warning" />}
                        <Typography variant="body2" color="text.secondary">
                          {getTicketDescription(child.ticket)}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={child.ticket.status === 'none' ? '없음' : child.ticket.days_left !== null && child.ticket.days_left <= 0 ? '만료' : '이용중'}
                      color={statusColor}
                      size="small"
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 알림장 */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                알림장
              </Typography>
              {(dashboardData?.unread_notice_count ?? 0) > 0 && (
                <Chip
                  label={`${dashboardData?.unread_notice_count}개 안읽음`}
                  color="error"
                  size="small"
                />
              )}
            </Box>
            <Button
              variant="text"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/parent/notice')}
            >
              바로가기
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
